/* eslint-disable max-len */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DataEntryMode } from '../../types/DataEntryMode';
import { BoardMembersFormViewModel } from '../../types/BoardMembersFormViewModel';
import { BoardMembersItemModel } from '../../types/BoardMembersItemModel';
import {
  BoardMembersListItemModel,
} from '../../types/BoardMembersListItemModel';
import { isEmptyObject } from '../../utilities/helperUtilities';
import { SelectOption } from '../../types/SelectOption';
import type { RootState } from '../store';
import {
  fetchBoardMembersListItems,
  fetchBoardMemberById,
  fetchBoardMembersFormViewModel,
  fetchStateOptions,
  fetchBoardMembersSchoolYears
} from '../thunks/boardMembersThunks';

interface BoardMembersState {
  schoolYear: SelectOption;
  loadingListItems: boolean;
  listItems: BoardMembersListItemModel[];
  dataEntryMode: DataEntryMode;
  updatedListItems: BoardMembersListItemModel[];
  loadingFormViewModel: boolean;
  formViewModel: BoardMembersFormViewModel;
  boardMember: BoardMembersItemModel;
  loadingBoardMember: boolean;
  schoolYears: SelectOption[];
  stateOptions: SelectOption[];
  loadingStateOptions: boolean;
  loadingSchoolYears: boolean;
}

export const defaultListState: BoardMembersListItemModel[] = [];

export const defaultBoardMembersListState: BoardMembersListItemModel = {
  id: '',
  lastOrSurname: '',
  firstName: '',
  email: '',
  positionType: '',
  termStartDate: '',
  termEndDate: '',
  leaIdentifier: '',
  schoolYear: '',
};

export const defaultBoardMembersItemState: BoardMembersItemModel = {
  id: '',
  firstName: '',
  lastOrSurname: '',
  streetNumberAndName: '',
  addressCity: '',
  addressStateAbbreviation: '',
  addressPostalCode: '',
  membershipType: '',
  positionType: '',
  leaIdentifier: '',
  termStartDate: '',
  termEndDate: '',
  schoolYear: '',
  deletedDateTime: '',
  deletedBy: '',
  email: '',
  apartmentRoomOrSuiteNumber: '',
};

export const defaultFormState: BoardMembersFormViewModel = {
  membershipType: [],
  positionType: [],
  stateOptions: [],
};

export const dataEntryMode = DataEntryMode.NEW;

export const initialState: BoardMembersState = {
  schoolYear: { value: '', text: '' },
  loadingListItems: false,
  loadingBoardMember: false,
  boardMember: defaultBoardMembersItemState,
  listItems: defaultListState,
  dataEntryMode: dataEntryMode,
  updatedListItems: defaultListState,
  loadingFormViewModel: false,
  formViewModel: defaultFormState,
  schoolYears: [],
  stateOptions: [],
  loadingStateOptions: false,
  loadingSchoolYears: false,
};

export const boardMembersSlice = createSlice({
  name: 'boardMembers',
  initialState,
  reducers: {
    clearBoardMembers: (state) => {
      state.boardMember = defaultBoardMembersItemState;
    },
    saveBoardMembers: (state, action: PayloadAction<BoardMembersItemModel>) => {
      state.boardMember = action.payload;
    },
    setBoardMembersDataEntryMode: (
      state,
      action: PayloadAction<DataEntryMode>
    ) => {
      state.dataEntryMode = action.payload;
    },
    setSchoolYearFilter: (state, action: PayloadAction<SelectOption>) => {
      state.schoolYear = action.payload;
    },
    setUpdatedListItems: (
      state,
      action: PayloadAction<BoardMembersListItemModel>
    ) => {
      const data = action.payload;
      state.updatedListItems.push(data);
    },
    setDefaultUpdatedListItems: (state) => {
      state.updatedListItems = [];
    },
    setIsListItemLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingListItems = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBoardMembersListItems.pending, (state) => {
      state.listItems = defaultListState;
      state.loadingListItems = true;
    });
    builder.addCase(fetchBoardMembersListItems.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.listItems = action.payload;
      }
      state.loadingListItems = false;
    });
    builder.addCase(fetchBoardMembersListItems.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingListItems = false;
      }
    });

    builder.addCase(fetchBoardMembersSchoolYears.pending, (state) => {
      state.schoolYears = [];
      state.loadingSchoolYears = true;
    });
    builder.addCase(fetchBoardMembersSchoolYears.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.schoolYears = action.payload;
        state.schoolYear = action.payload[0] ;
      }
      state.loadingSchoolYears = false;
    });
    builder.addCase(fetchBoardMembersSchoolYears.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingSchoolYears = false;
      }
    });

    builder.addCase(fetchBoardMemberById.pending, (state) => {
      state.boardMember = defaultBoardMembersItemState;
      state.loadingBoardMember = true;
    });
    builder.addCase(fetchBoardMemberById.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.boardMember = action.payload;
      }
      state.loadingBoardMember = false;
    });
    builder.addCase(fetchBoardMemberById.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingBoardMember = false;
      }
    });

    builder.addCase(fetchBoardMembersFormViewModel.pending, (state) => {
      state.formViewModel = defaultFormState;
      state.loadingFormViewModel = true;
    });
    builder.addCase(fetchBoardMembersFormViewModel.fulfilled, (state, action) => {    
      if (!isEmptyObject(action.payload)) {
        state.formViewModel = action.payload;
      }
      state.loadingFormViewModel = false;
    });
    builder.addCase(fetchBoardMembersFormViewModel.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingFormViewModel = false;
      }
    });

    builder.addCase(fetchStateOptions.pending, (state) => {
      state.stateOptions = [];
      state.loadingStateOptions = true;
    });
    builder.addCase(fetchStateOptions.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.stateOptions = action.payload;
      }
      state.loadingStateOptions = false;
    });
    builder.addCase(fetchStateOptions.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingStateOptions = false;
      }
    });

  },
});

//export reducers
export const {
  saveBoardMembers,
  clearBoardMembers,
  setSchoolYearFilter,
  setUpdatedListItems,
  setDefaultUpdatedListItems,
  setIsListItemLoading,
  setBoardMembersDataEntryMode
} = boardMembersSlice.actions;

export const selectSchoolYear = (state: RootState): SelectOption =>
  state.boardMembers.schoolYear;
export const selectUpdatedListItems = (
  state: RootState
): BoardMembersListItemModel[] => state.boardMembers.updatedListItems;

export const selectBoardMembersLoadingListItems = (
  state: RootState
): boolean => state.boardMembers.loadingListItems;
export const selectBoardMembersListItems = (
  state: RootState
): BoardMembersListItemModel[] => state.boardMembers.listItems;

// dataEntryMode
export const selectBoardMembersDataEntryMode = (
  state: RootState
): DataEntryMode => state.boardMembers.dataEntryMode;

// loadingBoardMember
export const selectLoadingBoardMember = (
  state: RootState
): boolean => state.boardMembers.loadingBoardMember;

export const selectLoadingSchoolYears = (
  state: RootState
): boolean => state.boardMembers.loadingSchoolYears;

// selectBoardMember
export const selectBoardMember = (
  state: RootState
): BoardMembersItemModel => state.boardMembers.boardMember;

export const selectBoardMembersFormViewModel = (
  state: RootState
): BoardMembersFormViewModel => state.boardMembers.formViewModel;

export const selectFilteredBoardMembersListItems = (
  state: RootState
): BoardMembersListItemModel[] => {
    if (!state.boardMembers.schoolYear) {
      return state.boardMembers.listItems;
    }
    return state.boardMembers.listItems.filter(item => {
        return item.schoolYear === state.boardMembers.schoolYear.value;
    });
}

export const selectSchoolYears = (
  state: RootState
): SelectOption[] => {
  return state.boardMembers.schoolYears;
};

export const selectStateOptions = (
  state: RootState
): SelectOption[] => {
  return state.boardMembers.stateOptions;
};

export default boardMembersSlice.reducer;
